
import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { 
  Step, 
  OnboardingState, 
  OnboardingContextType, 
  SubStep 
} from "@/lib/types";
import { 
  isLovableEditor,
  isAdminMode,
  areAllTodosCompleted,
} from "@/lib/utils";
import { onboardingSteps } from "@/data/onboardingSteps";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

const defaultState: OnboardingState = {
  currentStepIndex: 0,
  currentSubStepIndex: 0,
  currentDayIndex: 0,
  completedSteps: [],
  completedSubSteps: {},
  completedTodos: {},
  inputs: {},
  lastActiveDate: null,
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<OnboardingState>(defaultState);
  const [steps] = useState<Step[]>(onboardingSteps);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { user, saveOnboardingProgress } = useAuth();

  useEffect(() => {
    const adminMode = isLovableEditor() || isAdminMode();
    setIsAdmin(adminMode);
    setIsLoading(false);
  }, []);

  function getCurrentStep(): Step | undefined {
    return steps[state.currentStepIndex];
  }

  function getCurrentSubStep(): SubStep | undefined {
    const currentStep = getCurrentStep();
    if (!currentStep) return undefined;

    if (currentStep.type === 'multiStep' && currentStep.subSteps) {
      return currentStep.subSteps[state.currentSubStepIndex];
    }
    
    if (currentStep.type === 'dayStep' && currentStep.days) {
      const currentDay = currentStep.days[state.currentDayIndex];
      if (currentDay) {
        return currentDay.subSteps[state.currentSubStepIndex];
      }
    }

    return undefined;
  }

  const isTodoCompleted = (stepId: string, todoId: string): boolean => {
    return (state.completedTodos[stepId] || []).includes(todoId);
  };

  const toggleTodo = (stepId: string, todoId: string): void => {
    setState(prev => {
      const completedInStep = prev.completedTodos[stepId] || [];
      const isCompleted = completedInStep.includes(todoId);

      let newCompletedTodos;
      if (isCompleted) {
        newCompletedTodos = {
          ...prev.completedTodos,
          [stepId]: completedInStep.filter(id => id !== todoId)
        };
      } else {
        newCompletedTodos = {
          ...prev.completedTodos,
          [stepId]: [...completedInStep, todoId]
        };
        
        toast.success("Task completed!");
      }

      const newState = {
        ...prev,
        completedTodos: newCompletedTodos,
        lastActiveDate: new Date().toISOString().split('T')[0]
      };
      
      // Save progress to the server when toggling todos
      if (user?.email) {
        saveOnboardingProgress(user.email, newState);
      }
      
      return newState;
    });
  };

  function canProceed(): boolean {
    const currentStep = getCurrentStep();
    if (!currentStep) return false;

    const stepId = currentStep.id;
    
    if (currentStep.type === 'multiStep' && currentStep.subSteps) {
      const currentSubStep = currentStep.subSteps[state.currentSubStepIndex];
      if (currentSubStep?.todos) {
        return areAllTodosCompleted(
          currentSubStep.todos, 
          state.completedTodos, 
          stepId
        );
      }
    }
    
    if (currentStep.type === 'dayStep' && currentStep.days) {
      const currentDay = currentStep.days[state.currentDayIndex];
      if (currentDay) {
        const currentSubStep = currentDay.subSteps[state.currentSubStepIndex];
        if (currentSubStep?.todos) {
          return areAllTodosCompleted(
            currentSubStep.todos, 
            state.completedTodos, 
            stepId
          );
        }
      }
    }

    if (currentStep.todos) {
      const allTodosCompleted = areAllTodosCompleted(
        currentStep.todos, 
        state.completedTodos, 
        stepId
      );
      
      if (currentStep.inputs && currentStep.inputs.length > 0) {
        return allTodosCompleted && currentStep.inputs.every(input => 
          !!state.inputs[input.name]
        );
      }
      
      return allTodosCompleted;
    }

    if (currentStep.conditionalInput) {
      const todoCompleted = isTodoCompleted(
        stepId, 
        currentStep.conditionalInput.todoId
      );
      
      if (todoCompleted) {
        return !!state.inputs[currentStep.conditionalInput.input.name];
      }
    }

    if (currentStep.inputs && currentStep.inputs.length > 0) {
      return currentStep.inputs.every(input => 
        !!state.inputs[input.name]
      );
    }

    return true;
  }

  function proceedToNextStep(): void {
    setState(prev => {
      const currentStep = steps[prev.currentStepIndex];
      const completedSteps = [...prev.completedSteps];
      
      if (currentStep && !completedSteps.includes(currentStep.id)) {
        completedSteps.push(currentStep.id);
      }

      const newState = {
        ...prev,
        currentStepIndex: prev.currentStepIndex + 1,
        currentSubStepIndex: 0,
        currentDayIndex: 0,
        completedSteps,
        lastActiveDate: new Date().toISOString().split('T')[0]
      };
      
      // Save progress to the server
      if (user?.email) {
        saveOnboardingProgress(user.email, newState);
      }
      
      return newState;
    });
    
    toast.success("Moving to next step!");
    window.scrollTo(0, 0);
  }

  function proceedToNextSubStep(): void {
    setState(prev => {
      const currentStep = steps[prev.currentStepIndex];
      if (!currentStep) return prev;

      let newState = { ...prev };

      if (currentStep.type === 'multiStep' && currentStep.subSteps) {
        const completedSubSteps = { ...prev.completedSubSteps };
        const currentSubStep = currentStep.subSteps[prev.currentSubStepIndex];
        
        if (currentSubStep) {
          completedSubSteps[currentStep.id] = [
            ...(completedSubSteps[currentStep.id] || []),
            currentSubStep.id
          ];
        }

        if (prev.currentSubStepIndex >= currentStep.subSteps.length - 1) {
          const completedSteps = [...prev.completedSteps];
          if (!completedSteps.includes(currentStep.id)) {
            completedSteps.push(currentStep.id);
          }

          newState = {
            ...prev,
            currentStepIndex: prev.currentStepIndex + 1,
            currentSubStepIndex: 0,
            completedSteps,
            completedSubSteps,
            lastActiveDate: new Date().toISOString().split('T')[0]
          };
        } else {
          newState = {
            ...prev,
            currentSubStepIndex: prev.currentSubStepIndex + 1,
            completedSubSteps,
            lastActiveDate: new Date().toISOString().split('T')[0]
          };
        }
      } else if (currentStep.type === 'dayStep' && currentStep.days) {
        const currentDay = currentStep.days[prev.currentDayIndex];
        if (!currentDay) return prev;

        const completedSubSteps = { ...prev.completedSubSteps };
        const daySubStepKey = `${currentStep.id}_day${prev.currentDayIndex}`;
        const currentSubStep = currentDay.subSteps[prev.currentSubStepIndex];
        
        if (currentSubStep) {
          completedSubSteps[daySubStepKey] = [
            ...(completedSubSteps[daySubStepKey] || []),
            currentSubStep.id
          ];
        }

        if (prev.currentSubStepIndex >= currentDay.subSteps.length - 1) {
          completedSubSteps[`${currentStep.id}_day`] = [
            ...(completedSubSteps[`${currentStep.id}_day`] || []),
            `day${prev.currentDayIndex}`
          ];

          if (prev.currentDayIndex >= currentStep.days.length - 1) {
            const completedSteps = [...prev.completedSteps];
            if (!completedSteps.includes(currentStep.id)) {
              completedSteps.push(currentStep.id);
            }

            newState = {
              ...prev,
              currentStepIndex: prev.currentStepIndex + 1,
              currentSubStepIndex: 0,
              currentDayIndex: 0,
              completedSteps,
              completedSubSteps,
              lastActiveDate: new Date().toISOString().split('T')[0]
            };
          }

          return {
            ...prev,
            currentSubStepIndex: 0,
            completedSubSteps,
            lastActiveDate: new Date().toISOString().split('T')[0]
          };
        }

        return {
          ...prev,
          currentSubStepIndex: prev.currentSubStepIndex + 1,
          completedSubSteps,
          lastActiveDate: new Date().toISOString().split('T')[0]
        };
      } else {
        newState = {
          ...prev,
          currentStepIndex: prev.currentStepIndex + 1,
          currentSubStepIndex: 0,
          lastActiveDate: new Date().toISOString().split('T')[0]
        };
      }
      
      // Save progress to the server
      if (user?.email) {
        saveOnboardingProgress(user.email, newState);
      }
      
      return newState;
    });
    
    toast.success("Step completed!");
    window.scrollTo(0, 0);
  }

  function proceedToNextDay(): void {
    setState(prev => {
      const currentStep = steps[prev.currentStepIndex];
      if (!currentStep || currentStep.type !== 'dayStep' || !currentStep.days) {
        return prev;
      }

      let newState;
      if (prev.currentDayIndex < currentStep.days.length - 1) {
        newState = {
          ...prev,
          currentDayIndex: prev.currentDayIndex + 1,
          currentSubStepIndex: 0,
          lastActiveDate: new Date().toISOString().split('T')[0]
        };
      } else {
        newState = {
          ...prev,
          currentStepIndex: prev.currentStepIndex + 1,
          currentSubStepIndex: 0,
          currentDayIndex: 0,
          lastActiveDate: new Date().toISOString().split('T')[0]
        };
      }
      
      // Save progress to the server
      if (user?.email) {
        saveOnboardingProgress(user.email, newState);
      }
      
      return newState;
    });
    
    toast.success("Moving to next day!");
    window.scrollTo(0, 0);
  }

  const saveInput = (name: string, value: string): void => {
    setState(prev => {
      if (prev.inputs[name] === value) return prev;
      
      const newState = {
        ...prev,
        inputs: {
          ...prev.inputs,
          [name]: value
        },
        lastActiveDate: new Date().toISOString().split('T')[0]
      };
      
      // Save progress to the server when inputs change
      if (user?.email) {
        saveOnboardingProgress(user.email, newState);
      }
      
      return newState;
    });
  };

  const getInput = (name: string): string => {
    return state.inputs[name] || '';
  };

  const resetOnboarding = (): void => {
    setState(defaultState);
    toast.success("Progress has been reset");
  };

  const loadSavedState = (savedState: OnboardingState): void => {
    setState(savedState);
  };

  const contextValue: OnboardingContextType = {
    state,
    steps,
    isAdmin,
    getCurrentStep,
    getCurrentSubStep,
    isTodoCompleted,
    toggleTodo,
    canProceed,
    proceedToNextStep,
    proceedToNextSubStep,
    proceedToNextDay,
    saveInput,
    getInput,
    resetOnboarding,
    loadSavedState
  };

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading onboarding data...</div>;
  }

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
